"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KetteMath = void 0;
const constants_1 = require("../constants");
/**
 * Calculations related to Kette
 */
class KetteMath {
    /**
     * Calculates the maximum Verfahrweg for Freitragende Ketten
     *
     * @param zusatzlast_kg_m The additional weight of all Leitungen in the Kette
     * @param belastungsWerte_FZ_kg_m
     * @param belastungsWerte_FL_mm
     * @param versatz_mm
     * @returns the Verfahrweg
     */
    static calcMaxVerfahrwegFreitragend_mm(zusatzlast_kg_m, belastungsWerte_FZ_kg_m, belastungsWerte_FL_mm, versatz_mm) {
        if (!zusatzlast_kg_m || isNaN(zusatzlast_kg_m))
            return undefined;
        if (!belastungsWerte_FZ_kg_m.length || belastungsWerte_FZ_kg_m.length !== belastungsWerte_FL_mm.length)
            return undefined;
        const belastungsWerte = belastungsWerte_FZ_kg_m.map((v, i) => [v, belastungsWerte_FL_mm[i]]);
        if (belastungsWerte.length < 2)
            return undefined;
        const belastungsWert2 = belastungsWerte.slice(1).find((bw) => bw[0] <= zusatzlast_kg_m);
        if (!belastungsWert2)
            return undefined;
        const indexOfBelastungsWert2 = belastungsWerte.indexOf(belastungsWert2);
        if (indexOfBelastungsWert2 === -1)
            return undefined;
        const belastungsWert1 = belastungsWerte[indexOfBelastungsWert2 - 1];
        if (!belastungsWert1 || zusatzlast_kg_m > belastungsWert1[0])
            return undefined;
        let freitragendeLaenge = 0;
        if (belastungsWert2[0] - belastungsWert1[0] === 0) {
            freitragendeLaenge = belastungsWert2[1];
        }
        else {
            freitragendeLaenge =
                ((zusatzlast_kg_m - belastungsWert1[0]) / (belastungsWert2[0] - belastungsWert1[0])) *
                    (belastungsWert2[1] - belastungsWert1[1]) +
                    belastungsWert1[1];
        }
        if (versatz_mm < 0) {
            // we have to increase the fl by the abs(versatz) in case of negative value.
            // that's why we just reduce the max verfahrweg by the abs(versatz)
            // => max verfahrweg = 2 * freitragendeLaenge - abs(versatz_mm)
            return freitragendeLaenge * 2 - Math.abs(versatz_mm);
        }
        else {
            // max verfahrweg = 2 * freitragendeLaenge
            return freitragendeLaenge * 2;
        }
    }
    /**
     * Calculates the maxZusatzlast for a Gleitende Kette
     *
     * @param geschwindikeit_m_s The speed of the Kette
     * @param gleitendFZs The workload values for Ketten
     * @returns the maximum Zusatzlast
     */
    static calcMaxZusatzlastGleitend_kg_m(geschwindikeit_m_s, gleitendFZs) {
        if (geschwindikeit_m_s && gleitendFZs && gleitendFZs.length > 0) {
            let maxZusatzlast = 0;
            let zusatzlast0 = 0;
            let geschwindigkeit0 = 0;
            let zusatzlast1 = 0;
            let geschwindigkeit1 = 0;
            for (let i = 0; i < gleitendFZs.length; i++) {
                if (i === 0) {
                    zusatzlast0 = gleitendFZs[0][0];
                    geschwindigkeit0 = 0;
                    zusatzlast1 = gleitendFZs[i][0];
                    geschwindigkeit1 = gleitendFZs[i][1];
                }
                else {
                    zusatzlast0 = gleitendFZs[i - 1][0];
                    geschwindigkeit0 = gleitendFZs[i - 1][1];
                    zusatzlast1 = gleitendFZs[i][0];
                    geschwindigkeit1 = gleitendFZs[i][1];
                }
                if (geschwindikeit_m_s <= geschwindigkeit1) {
                    break;
                }
            }
            if (geschwindikeit_m_s >= geschwindigkeit0 && geschwindikeit_m_s <= geschwindigkeit1) {
                if (zusatzlast1 - zusatzlast0 === 0) {
                    maxZusatzlast = zusatzlast1;
                }
                else {
                    maxZusatzlast =
                        zusatzlast0 +
                            ((zusatzlast1 - zusatzlast0) * (geschwindikeit_m_s - geschwindigkeit0)) /
                                (geschwindigkeit1 - geschwindigkeit0);
                }
            }
            return maxZusatzlast;
        }
        return undefined;
    }
    /**
     * Calculates the effective length by the amount and length of the Glieder.
     *
     * @param numGlieder The amount of Glieder
     * @param teilung_mm The length of a single Glied
     * @returns the effective length in mm
     */
    static calcEffektiveLaenge_mm(numGlieder, teilung_mm) {
        return numGlieder * teilung_mm;
    }
    /**
     * Calculates the effective length by the amount and length of the Glieder on base of the Verfahrweg.
     *
     * @param verfahrweg_mm The Verfahrweg of the Kette
     * @param k_mm The unit K of the Kette
     * @param versatz_mm The versatz of the Festpunkt
     * @param teilung_mm The length of a single Glied
     * @returns The effective length
     */
    static calcEffektiveLaengeFromVerfahrweg_mm(verfahrweg_mm, k_mm, versatz_mm, teilung_mm) {
        const laengeWithVersatz_mm = KetteMath.calcLaenge_mm(verfahrweg_mm, k_mm, versatz_mm);
        const numGlieder = KetteMath.calcNumGlieder_1(laengeWithVersatz_mm, teilung_mm);
        const effektiveLaenge_mm = KetteMath.calcEffektiveLaenge_mm(numGlieder, teilung_mm);
        return effektiveLaenge_mm;
    }
    /**
     * Calculates the effective length for long distance applications (Langer Weg) by the amount and length of the Glieder on base of the Verfahrweg.
     *
     * @param verfahrweg_mm The Verfahrweg of the Kette
     * @param k2_mm The unit K2 of the Kette
     * @param versatz_mm The Versatz of the Festpunkt
     * @param teilung_mm The length of a single Glied
     * @returns The effective length
     */
    static calcEffektiveLaengeFromVerfahrwegWithLangerWeg_mm(verfahrweg_mm, k2_mm, versatz_mm, teilung_mm) {
        const laengeWithVersatz_mm = KetteMath.calcLaengeWithLangerWeg_mm(verfahrweg_mm, k2_mm, versatz_mm);
        const numGlieder = KetteMath.calcNumGlieder_1(laengeWithVersatz_mm, teilung_mm);
        const effektiveLaenge_mm = KetteMath.calcEffektiveLaenge_mm(numGlieder, teilung_mm);
        return effektiveLaenge_mm;
    }
    /**
     * Calculates the minimal (not effective) length of the kette.
     * The effective length depends on the Teilung of a Glied.
     *
     * @param verfahrweg_mm The Verfahrweg of the Kette
     * @param k_mm The unit K of the Kette
     * @param versatz_mm The Versatz of the Festpunkt
     * @returns The length
     */
    static calcLaenge_mm(verfahrweg_mm, k_mm, versatz_mm) {
        return verfahrweg_mm * 0.5 + k_mm + Math.abs(versatz_mm);
    }
    /**
     * Calculates the minimal (not effective) length of the Kette for long distance (Langer Weg) applications.
     * The effective length depends on the Teilung of a Glied.
     *
     * @param verfahrweg_mm The Verfahrweg of the Kette
     * @param k2_mm The unit K2 of the Kette
     * @param versatz_mm The Versatz of the Festpunkt
     * @returns The length
     */
    static calcLaengeWithLangerWeg_mm(verfahrweg_mm, k2_mm, versatz_mm) {
        return verfahrweg_mm * 0.5 + k2_mm + Math.abs(versatz_mm);
    }
    /**
     * Calculates the Verfahrweg based on the effective length.
     *
     * @param effektiveLaenge_mm The effective length of the Kette considering the Teilung
     * @param k_mm The unit k of the Kette
     * @param versatz_mm The Versatz of the Festpunkt
     * @returns the Verfahrweg of the Kette
     */
    static calcVerfahrweg_mm(effektiveLaenge_mm, k_mm, versatz_mm) {
        const verfahrweg_mm = 2 * (effektiveLaenge_mm - Math.abs(versatz_mm) - k_mm);
        return Math.max(0, verfahrweg_mm);
    }
    /**
     * Calculates the Verfahrweg for long distance (langer Weg) applications based on the effective length.
     *
     * @param effektiveLaenge_mm The effective length of the Kette considering the Teilung
     * @param k2_mm The unit K2 of the Kette
     * @param versatz_mm The Versatz of the Festpunkt
     * @returns the Verfahrweg of the Kette
     */
    static calcVerfahrwegWithLangerWeg_mm(effektiveLaenge_mm, k2_mm, versatz_mm) {
        const verfahrweg_mm = 2 * (effektiveLaenge_mm - Math.abs(versatz_mm) - k2_mm);
        return Math.max(0, verfahrweg_mm);
    }
    /**
     * Calculates the freitragende Laenge based on verfahrweg and versatz.
     */
    static calcFreitragendeLaenge_mm(verfahrweg_mm, versatz_mm) {
        if (versatz_mm < 0) {
            return verfahrweg_mm / 2 + Math.abs(versatz_mm);
        }
        else {
            return verfahrweg_mm / 2;
        }
    }
    /**
     * Calculates the amount of required Glieder to reach the length.
     *
     * @param laenge_mm The minimal length of the Kette
     * @param teilung_mm The length of a Glied
     * @returns the amount of Glieder
     */
    static calcNumGlieder_1(laenge_mm, teilung_mm) {
        return Math.ceil(laenge_mm / teilung_mm);
    }
    /**
     * Calculates the amount of required Glieder to reach the length without rounding up.
     *
     * @param laenge_mm The minimal length of the Kette
     * @param teilung_mm The length of a Glied
     * @returns the amount of Glieder
     */
    static calcNumGlieder_float(laenge_mm, teilung_mm) {
        return laenge_mm / teilung_mm;
    }
    /**
     * Calculates the maximum push-pull-force for Gleitende Anwendungen.
     *
     * @param zusatzlast_kg_m The additional weight of all Leitungen in the kette
     * @param ketteGewichtKgProM_kg_m The weight of the Kette
     * @param effektiveLaenge_mm The effective length of the Kette considering the Teilung
     * @param beschleunigungMax_m_s2 The maximum acceleration of the Kette
     * @param frictionFactor The friction factor for the Kette
     * @returns the maximum push-pull-force
     */
    static calcMaxPushPullForceGleitend_N(zusatzlast_kg_m, ketteGewichtKgProM_kg_m, effektiveLaenge_mm, beschleunigungMax_m_s2, frictionFactor) {
        const effektiveLaenge_m = effektiveLaenge_mm / 1000;
        return ((zusatzlast_kg_m + ketteGewichtKgProM_kg_m) *
            effektiveLaenge_m *
            (beschleunigungMax_m_s2 + frictionFactor * constants_1.GRAVITY_M_S2));
    }
    /**
     * Calculates the maximum push-pull-force for Haengende Anwendungen.
     *
     * @param zusatzlast_kg_m The additional weight of all leitungen in the Kette
     * @param ketteGewichtKgProM_kg_m The weight of the Kette
     * @param effektiveLaenge_mm The effective length of the Kette considering the Teilung
     * @param beschleunigungMax_m_s2 The maximum acceleration of the Kette
     * @returns the maximum push-pull-force
     */
    static calcMaxPushPullForceHaengend_N(zusatzlast_kg_m, ketteGewichtKgProM_kg_m, effektiveLaenge_mm, beschleunigungMax_m_s2, frictionFactor) {
        const effektiveLaenge_m = effektiveLaenge_mm / 1000;
        return ((zusatzlast_kg_m + ketteGewichtKgProM_kg_m) *
            effektiveLaenge_m *
            (beschleunigungMax_m_s2 + frictionFactor * constants_1.GRAVITY_M_S2));
    }
}
exports.KetteMath = KetteMath;
//# sourceMappingURL=kette-math.js.map